import {
  Bell,
  ShieldAlert,
  ClipboardCheck,
  Wallet,
  FileText,
  Wrench,
  Leaf,
  Boxes,
  UserCog,
  LogIn,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import type { NotificationItem } from '../services/notifications.service';

export interface NotificationVisual {
  icon: LucideIcon;
  /** Tailwind classes for the icon's circular chip. */
  chipClassName: string;
}

/**
 * Map a notification type / messageType to an icon + colour by keyword, so the
 * panel reads at a glance without a hand-maintained entry per (87+) message type.
 */
export function notificationVisual(type: string | undefined): NotificationVisual {
  const t = (type ?? '').toUpperCase();
  const has = (...keys: string[]) => keys.some((k) => t.includes(k));

  if (has('LOGIN', 'AUTH', 'ACCOUNT', 'PASSWORD', 'OTP', 'SECURITY', 'LOCK'))
    return { icon: LogIn, chipClassName: 'bg-muted text-text-body' };
  if (has('SAFETY', 'INCIDENT', 'MISFIRE', 'HAZARD', 'CORRECTIVE', 'INSPECTION'))
    return { icon: ShieldAlert, chipClassName: 'bg-destructive/10 text-destructive' };
  if (has('COMPLIANCE', 'LICENSE', 'PERMIT', 'COMMITMENT', 'KYB', 'AUDIT'))
    return { icon: ClipboardCheck, chipClassName: 'bg-warning-surface text-warning' };
  if (has('PAYMENT', 'PAYROLL', 'WALLET', 'WITHDRAW', 'COMPENSATION', 'FINANCE'))
    return { icon: Wallet, chipClassName: 'bg-success-surface text-success' };
  if (has('REPORT'))
    return { icon: FileText, chipClassName: 'bg-primary-50 text-primary' };
  if (has('EQUIPMENT', 'BREAKDOWN'))
    return { icon: Wrench, chipClassName: 'bg-muted text-text-body' };
  if (has('ENVIRON', 'TAILINGS', 'WASTE', 'EMISSION'))
    return { icon: Leaf, chipClassName: 'bg-success-surface text-success' };
  if (
    has('STOCK', 'INVENTORY', 'REQUISITION', 'LOT', 'CUSTODY', 'MEU',
      'PRODUCTION', 'TUNNEL', 'SHIFT', 'BLAST', 'DISPATCH')
  )
    return { icon: Boxes, chipClassName: 'bg-primary-50 text-primary' };
  if (has('ROLE', 'PERMISSION', 'USER', 'WORKER', 'ONBOARD', 'INVITE', 'ATTENDANCE'))
    return { icon: UserCog, chipClassName: 'bg-muted text-text-body' };
  if (has('TENANT', 'MAINTENANCE', 'SYSTEM'))
    return { icon: AlertTriangle, chipClassName: 'bg-warning-surface text-warning' };
  return { icon: Bell, chipClassName: 'bg-muted text-text-body' };
}

function readString(source: unknown, key: string): string | undefined {
  if (source && typeof source === 'object') {
    const value = (source as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

/**
 * Best-effort in-app navigation target from a notification's metadata. Backend
 * payloads carry the link at a few known spots (callToAction.urlPath is the
 * canonical one; report.urlPath and content.ctaUrl are legacy variants).
 */
export function notificationLink(item: NotificationItem): string | undefined {
  const m = item.metadata;
  if (!m || typeof m !== 'object') return undefined;
  const meta = m;
  return (
    readString(meta['callToAction'], 'urlPath') ??
    readString(meta['report'], 'urlPath') ??
    readString(meta['content'], 'ctaUrl') ??
    readString(meta['content'], 'urlPath') ??
    readString(meta, 'urlPath') ??
    readString(meta, 'ctaUrl')
  );
}
